import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn';

/**
 * Marca de 29100 Cloud.
 *
 * La idea, en una frase: **el número crece hasta convertirse en nube**. Las
 * cinco cifras de 29100 aumentan de tamaño de izquierda a derecha, y la quinta
 * no está escrita —está dibujada, y resulta que se llama igual que el
 * producto—. Los tres lóbulos de la nube crecen con esa misma regla, que es lo
 * que hace que el conjunto se lea como un movimiento y no como un número con
 * un icono al lado.
 *
 * Hay dos piezas y cada una tiene su trabajo:
 *
 *   * `Logo` — la nube sola más el nombre. Es la que va en cabecera, pie y
 *     pantallas de acceso, y la que aguanta 16 px en una pestaña.
 *   * `BrandMark` — el logotipo entero con las cinco cifras. Necesita ancho,
 *     así que vive en portada y campaña, nunca en una barra de navegación:
 *     ahí las cifras aparecerían dos veces, en el dibujo y en la palabra.
 *
 * Vive en un único sitio a propósito: el SVG duplicado en cada página es lo
 * que hace que un rediseño de marca se convierta en una cacería.
 */

/**
 * Los tres lóbulos, en el orden en que crecen.
 *
 * Estaban escritos a mano, uno por `<circle>`, y cada cosa que se les pide
 * —la deriva, ahora la reacción al puntero— obligaba a repetir la misma línea
 * tres veces con un número distinto. En una tabla, la regla que gobierna el
 * dibujo se lee de un vistazo: el radio, el alza y el desfase crecen juntos.
 *
 * El lóbulo grande es (64, 50) r25 y no (66, 43) r22 por una razón que sólo
 * se ve a tamaño de portada: con el radio anterior, el borde derecho del
 * círculo se metía hacia dentro justo donde la barra volvía a salir, y la
 * unión de las dos figuras dejaba un pellizco cóncavo —un mordisco— en el
 * costado. El lóbulo nuevo cubre la barra en todo su alto, así que el perfil
 * derecho baja de un solo trazo. El dibujo es el mismo que `public/icon.svg`:
 * si se toca uno hay que tocar el otro, o el favicon deja de ser esta nube.
 */
const LOBULOS = [
  {
    cx: 21,
    cy: 53,
    r: 13,
    /** Desfase de la deriva continua, sumado a `ENTRADA_TOTAL`. */
    deriva: 0,
    /** Desfase de la reacción al puntero: la ola viaja hacia donde crece. */
    reaccion: 0,
    /*
     * El alza crece con el lóbulo, igual que el radio. Escrita entera y no
     * interpolada porque Tailwind lee el código como texto: una clase montada
     * con plantilla no existe en la hoja de estilos y el lóbulo no se movería.
     */
    alza: 'group-hover:-translate-y-[3px] group-focus-visible:-translate-y-[3px]',
  },
  {
    cx: 43,
    cy: 49,
    r: 19,
    deriva: 900,
    reaccion: 70,
    alza: 'group-hover:-translate-y-[4.5px] group-focus-visible:-translate-y-[4.5px]',
  },
  {
    cx: 64,
    cy: 50,
    r: 25,
    deriva: 1800,
    reaccion: 140,
    alza: 'group-hover:-translate-y-[6px] group-focus-visible:-translate-y-[6px]',
  },
] as const;

/**
 * La nube, en su lienzo de 96×96.
 *
 * Cuatro primitivas macizas —una barra y tres círculos que crecen— en vez de
 * un contorno. Un trazo fino se adelgaza hasta desaparecer al reducir y hay
 * que redibujarlo para vinilo o grabado; una silueta llena no tiene nada que
 * perder, y por eso ésta es la pieza que sobrevive al favicon.
 *
 * Se mueve de dos maneras que no se mezclan nunca. `animated` es la deriva
 * continua de la portada; `interactive` es la ola que sube por los lóbulos
 * cuando el puntero —o el teclado— llega a la marca, y vuelve al soltarla.
 * En una barra lateral que se mira ocho horas, la primera sería ruido: un
 * logotipo que no para de moverse deja de ser una marca y pasa a ser algo que
 * se mueve en el rabillo del ojo. La segunda sólo aparece si la pides.
 */
function Nube({
  animated = false,
  interactive = false,
}: {
  animated?: boolean;
  /**
   * La nube responde al puntero y al foco. Excluyente con `animated`: una
   * animación en curso **sustituye** al `transform` de la transición, así que
   * un lóbulo que ya deriva se quedaría sordo al ratón.
   */
  interactive?: boolean;
}) {
  const reacciona = interactive && !animated;

  return (
    <g fill="rgb(var(--c-brand-5))">
      <rect x="8" y="53" width="80" height="22" rx="11" />
      {LOBULOS.map((lobulo) => (
        <circle
          key={lobulo.r}
          cx={lobulo.cx}
          cy={lobulo.cy}
          r={lobulo.r}
          className={cn(
            animated && 'animate-brand-float',
            reacciona && `transition-transform duration-500 ease-out ${lobulo.alza}`,
          )}
          style={
            animated
              ? { animationDelay: `${ENTRADA_TOTAL + lobulo.deriva}ms` }
              : reacciona
                ? { transitionDelay: `${lobulo.reaccion}ms` }
                : undefined
          }
        />
      ))}
    </g>
  );
}

/**
 * La marca de cabecera, pie y barra lateral.
 *
 * Reacciona al puntero y al foco **sólo cuando lleva a algún sitio**. Un
 * dibujo que se anima al pasar por encima está diciendo «púlsame»: si además
 * no es un enlace, promete algo que no cumple, y el usuario acaba haciendo
 * clic en un logotipo que no hace nada. Por eso `interactive` se deduce de
 * `to` en lugar de estar encendido siempre, y se puede forzar para los casos
 * raros.
 *
 * La reacción tiene tres partes y todas son CSS, sin estado ni `onMouseEnter`:
 * un logotipo que provoca un renderizado de React cada vez que el ratón lo
 * cruza es un coste que se paga en toda la aplicación para no ganar nada.
 *
 *   1. Los lóbulos suben escalonados, cada uno un poco más que el anterior.
 *      La ola viaja en la misma dirección en la que crece la marca, que es la
 *      idea del logotipo: el número creciendo hasta convertirse en nube.
 *   2. El «cloud» apagado sube a color de texto pleno.
 *   3. Al pulsar, la marca entera se hunde un píxel.
 *
 * Quien haya pedido menos movimiento no recibe ninguno: la regla
 * `prefers-reduced-motion` de `styles.css` deja las transiciones en 0,01 ms,
 * así que el estado cambia igual pero sin recorrido. Es la misma red que ya
 * cubría a `animated`, y por eso aquí no hay nada que comprobar.
 */
export function Logo({
  size = 'md',
  showWordmark = true,
  to = '/',
  className,
  interactive,
}: {
  size?: 'sm' | 'md' | 'lg';
  showWordmark?: boolean;
  /** `null` renderiza la marca sin enlace (útil dentro de otro enlace). */
  to?: string | null;
  className?: string;
  /** Por defecto, reacciona si es enlace. */
  interactive?: boolean;
}) {
  const dimension = { sm: 'h-7 w-7', md: 'h-9 w-9', lg: 'h-12 w-12' }[size];
  const texto = { sm: 'text-base', md: 'text-lg', lg: 'text-2xl' }[size];
  const reacciona = interactive ?? to !== null;

  const marca = (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      {/*
        El leve crecimiento va aquí, en el `<svg>`, y no en sus hijos: un
        `scale` sobre una pieza de dentro toma como origen la esquina del
        lienzo y descoloca el dibujo, mientras que el `<svg>` es una caja del
        documento como cualquier otra y crece desde su centro.
      */}
      <svg
        viewBox="0 0 96 96"
        className={cn(
          dimension,
          'shrink-0',
          reacciona &&
            'transition-transform duration-500 ease-out group-hover:scale-105 group-focus-visible:scale-105',
        )}
        role="img"
        aria-label="29100 Cloud"
      >
        <Nube interactive={reacciona} />
      </svg>

      {/*
        `whitespace-nowrap` porque el espacio fino entre «29100» y «cloud» es
        divisible: sin esto, en una cabecera estrecha el logotipo se parte en
        dos líneas y deja de ser un logotipo.
      */}
      {showWordmark ? (
        <span className={cn('whitespace-nowrap font-bold tracking-tighter', texto)}>
          29100
          <span
            className={cn(
              'font-normal tracking-tight text-muted',
              reacciona &&
                'transition-colors duration-300 group-hover:text-content group-focus-visible:text-content',
            )}
          >
            &thinsp;cloud
          </span>
        </span>
      ) : null}
    </span>
  );

  if (!to) return marca;

  return (
    <Link
      to={to}
      className={cn(
        'group inline-flex rounded-xl',
        reacciona && 'transition-transform duration-100 active:translate-y-px',
      )}
      aria-label="29100 Cloud — inicio"
    >
      {marca}
    </Link>
  );
}

/**
 * Las cuatro primeras cifras, dibujadas y no escritas.
 *
 * Estaban puestas con `<text>`, y eso hacía que el logotipo no fuese un
 * dibujo sino una sugerencia: el navegador lo componía con la tipografía que
 * tuviera a mano, y el ancho de cada cifra —que es lo que decide dónde cae la
 * siguiente— cambia de una fuente a otra. El resultado se puede ver: en
 * Windows quedaba un hueco largo entre el «1» y el «0», y en Linux el «1» se
 * comía el aro. Además `Inter`, que es la fuente que el tema declara, no se
 * carga en ninguna parte, así que ni siquiera era la fuente prevista la que
 * salía. Un logotipo que se dibuja distinto en cada sistema operativo no es
 * un logotipo.
 *
 * Ahora las cuatro son trazos de grosor constante con extremos redondos, el
 * mismo vocabulario con el que ya estaban hechos el «0» y la nube. Se ganan
 * dos cosas de paso: el dibujo es idéntico en todas partes, y el logotipo
 * entero —cifras, aro y nube— pasa a estar hecho de una sola familia de
 * formas en lugar de mezclar tipografía con geometría.
 *
 * Las medidas no son a ojo. Todas las cifras se apoyan en la línea base 72;
 * el alto y el grosor crecen a razón de ~1,35 por cifra (18 · 25 · 33 · 42 y
 * 50 la nube, con el trazo al 21 % del alto), y el hueco entre piezas crece
 * con ellas (6 · 8 · 10 · 12) para que la aceleración se lea también en el
 * aire que las separa. El ojo del «9» y el del «0» tienen la misma proporción
 * de hueco —cerca de la mitad del diámetro—, que es lo que evita que el
 * pequeño se empaste al reducir.
 */
const CIFRAS = [
  { cifra: '2', d: 'M10 60.2A4.2 4.2 0 1 1 18.4 60.2L10 70L18.4 70', grosor: 4, tono: 1 },
  {
    cifra: '9',
    d: 'M43 56.6A7 7 0 1 1 29 56.6A7 7 0 1 1 43 56.6M43 56.6L43 69.4',
    grosor: 5.2,
    tono: 2,
  },
  { cifra: '1', d: 'M57.1 48.2L65.1 42.5L65.1 68.5', grosor: 7, tono: 3 },
  {
    // El cuarto dígito ya es un círculo: prepara el ojo para la nube.
    cifra: '0',
    d: 'M115.6 51A16 16 0 1 1 83.6 51A16 16 0 1 1 115.6 51',
    grosor: 10,
    tono: 4,
  },
] as const;

/** Un escalón de entrada por pieza, en el orden en que crecen. */
const RETARDO = 110;

/**
 * El alza de cada pieza al pasar el puntero, en el orden en que crecen.
 *
 * Es la misma ola que hace la nube en la cabecera, pero recorriendo el
 * logotipo entero: el «2» apenas se despega y la nube sube el triple. La
 * regla del dibujo —cada figura mayor que la anterior— se cuenta así también
 * en el movimiento, en lugar de mover las cinco piezas por igual, que es lo
 * que haría de esto un adorno.
 *
 * Las cifras son pequeñas sobre un lienzo de 90 de alto: 4,5 unidades son un
 * 5 % de la altura de la marca, se vean a 17 rem o a tamaño de portada.
 * Escritas enteras y no interpoladas porque Tailwind lee el código como
 * texto: una clase montada con plantilla no llega a la hoja de estilos.
 */
const ALZAS = [
  'group-hover:-translate-y-[1.5px]',
  'group-hover:-translate-y-[2.25px]',
  'group-hover:-translate-y-[3px]',
  'group-hover:-translate-y-[3.75px]',
  'group-hover:-translate-y-[4.5px]',
] as const;

/** Escalón de la ola de reacción. Más corto que el de entrada: no es una presentación. */
const RETARDO_REACCION = 60;
/** Lo que tarda la marca entera en terminar de aparecer: 5 piezas y la última. */
const ENTRADA_TOTAL = 4 * RETARDO + 760;

/**
 * El logotipo completo: `2 9 1 0` y la nube como quinta cifra.
 *
 * `animated` sólo debería activarse donde la marca es grande y se ve entera
 * —la portada—. La deriva de los lóbulos es continua, y una animación que no
 * para dentro de una barra de navegación deja de ser una marca para
 * convertirse en una distracción. Quien haya pedido menos movimiento no recibe
 * ninguno: la regla `prefers-reduced-motion` de `styles.css` apaga las dos
 * animaciones sin que este componente tenga que enterarse.
 *
 * El movimiento es el mismo de siempre —cada pieza sube a su turno y luego
 * los lóbulos derivan— con un solo arreglo: la deriva arranca cuando la
 * entrada ha terminado (`ENTRADA_TOTAL`) y no en el instante cero. Antes los
 * dos primeros lóbulos ya estaban flotando mientras la nube todavía se
 * desvanecía hacia dentro, y las dos animaciones se pisaban el `transform`
 * en mitad de la aparición.
 */
export function BrandMark({
  className,
  animated = false,
  interactive = false,
  title = '29100 Cloud',
}: {
  className?: string;
  animated?: boolean;
  /**
   * La marca responde al puntero: las cinco piezas suben escalonadas, cada
   * una más que la anterior. Se combina con `animated` sin estorbarlo —a
   * diferencia de lo que ocurre en la nube de cabecera, donde son excluyentes—
   * porque aquí la reacción y la animación viven en elementos distintos.
   */
  interactive?: boolean;
  title?: string;
}) {
  const retardo = (indice: number) =>
    animated ? { animationDelay: `${indice * RETARDO}ms` } : undefined;
  const entrada = animated ? 'animate-brand-rise' : undefined;

  /*
   * La reacción ENVUELVE a la pieza, no se le pone encima.
   *
   * `brand-rise` está declarada con `both`, así que al terminar no suelta el
   * `transform`: lo sigue fijando en su fotograma final para siempre. Una
   * transición sobre ese mismo elemento no ganaría nunca —la animación tiene
   * precedencia— y el ratón no movería nada, sin error en ninguna parte.
   *
   * En un `<g>` aparte los dos `transform` se multiplican en lugar de
   * disputarse, que es exactamente el mismo motivo por el que la traslación
   * de la nube ya vivía en su propio grupo.
   */
  const reaccion = (indice: number) =>
    interactive
      ? {
          className: cn('transition-transform duration-500 ease-out', ALZAS[indice]),
          style: { transitionDelay: `${indice * RETARDO_REACCION}ms` },
        }
      : {};

  return (
    <svg
      viewBox="0 0 222 90"
      className={cn(className, interactive && 'group')}
      role="img"
      aria-label={title}
    >
      {CIFRAS.map((pieza, indice) => (
        <g key={pieza.cifra} {...reaccion(indice)}>
          <path
            d={pieza.d}
            fill="none"
            stroke={`rgb(var(--c-brand-${pieza.tono}))`}
            strokeWidth={pieza.grosor}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={entrada}
            style={retardo(indice)}
          />
        </g>
      ))}

      {/*
        La misma nube del favicon, sin escalar: se traslada para que su base
        caiga sobre la línea de las cifras. Dibujarla dos veces con medidas
        distintas es como acaban las marcas cuyo icono no se parece al logo.

        Son grupos anidados a propósito, y no uno con todo: una animación CSS
        que toca `transform` **sustituye** al atributo `transform` del SVG en
        lugar de sumarse a él. Con ambos en el mismo `<g>`, la nube perdía su
        traslación al terminar la entrada, se dibujaba en el origen y tapaba
        las cifras. La reacción al puntero se apila por encima por lo mismo.
      */}
      <g {...reaccion(CIFRAS.length)}>
        <g transform="translate(124.6 -3)">
          <g className={entrada} style={retardo(CIFRAS.length)}>
            <Nube animated={animated} />
          </g>
        </g>
      </g>
    </svg>
  );
}

export const TAGLINE = 'Kept for 29100.';
